import { inject, Injectable } from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { values } from 'lodash';
import { KeyboardShortcutName } from '../../../../../../cross-team-shared/misc-services/keyboard-shortcut.service';
import { EventTrackingValueWithSource } from '../../../../../../cross-team-shared/misc-utils';
import { isSensorFusionOutput } from '../../../../../../cross-team-shared/misc-utils/output-utils';
import { toggleState } from '../../../../../../shared/misc-utilities/store-utils';
import { SettingsService } from '../../../../../h3ml/dsl/sensor-fusion/view-settings/settings.service';
import { TaskWorkspaceStore } from '../../../../../h3ml/state/task-workspace-store';
import { RootKeyboardShortcutService } from '../../../../../shared/keyboard-shortcuts/root-keyboard-shortcut.service';
import { FusionManagerFactory } from '../../fusion/fusion-manager-factory.service';
import {
  filterSelectionByOutput,
  RootShapeSelectionService,
} from '../../utils/root-shape-selection.service';

@Injectable()
export class CVToolbarKeyboardShortcutService extends InjectableSuperclass {
  private readonly taskWorkspaceStore = inject(TaskWorkspaceStore);
  private readonly settingsStore = this.taskWorkspaceStore('settings');

  private fusionManagerFactory = inject(FusionManagerFactory);
  private rootShortcutService = inject(RootKeyboardShortcutService);
  private selectionService = inject(RootShapeSelectionService);
  private settingsService = inject(SettingsService);

  register() {
    this.unsubscribe();

    this.setUpToggleOrthographicCamera();

    this.setUpSubscriptionToShortcut('resetSensorView', (eventTrackingValue) =>
      this.settingsService.resetSensorView(eventTrackingValue),
    );
    this.setUpSubscriptionToShortcut('cycleViewMode', (eventTrackingValue) =>
      this.settingsService.toggleViewMode(eventTrackingValue),
    );
    this.setUpSubscriptionToShortcut(
      'toggleInspectMode',
      (eventTrackingValue) =>
        this.settingsService.toggleInspectMode(eventTrackingValue),
    );
    this.setUpSubscriptionToShortcut(
      'toggleEnhancedPointCloud',
      (eventTrackingValue) =>
        this.settingsService.toggleEnhancedPointCloud(eventTrackingValue),
    );

    if (this.taskWorkspaceStore.isUsingObjects()) {
      this.setUpSubscriptionToShortcut('toggleZoomPanel', () => {
        if (!this.settingsStore.state().zoomPanel.isOpen) {
          this.settingsService.toggleZoomPanel('shortcut');
        }
      });
    }
  }

  private setUpToggleOrthographicCamera() {
    this.setUpSubscriptionToShortcut(
      'toggleOrthographicCamera',
      (eventTrackingValue) => {
        const selectedShapes = this.getPointCloudSelectedShapes();
        const hasPointCloudShapeFocused = values(selectedShapes).length === 1;

        const isSplitViewActive = isDefined(
          this.taskWorkspaceStore.state().splitViewOutputName,
        );
        if (isSplitViewActive && hasPointCloudShapeFocused) {
          const topViewHiddenStore = this.settingsStore(
            'topViewHiddenInSplitView',
          );
          toggleState(topViewHiddenStore);
          const { useOrthographicCamera } = this.settingsStore.state();
          if (useOrthographicCamera) {
            this.settingsStore('useOrthographicCamera').set(false);
          }
          this.settingsService.trackMainCameraTypeEvent({
            ...eventTrackingValue,
            new_value: topViewHiddenStore.state()
              ? 'perspective'
              : 'shape_orthographic',
          });
        } else {
          this.settingsService.toggleOrthographicCamera(eventTrackingValue);
        }
      },
    );
  }

  private setUpSubscriptionToShortcut(
    shortcutName: KeyboardShortcutName,
    handleEventCallback: (
      eventTrackingValue: EventTrackingValueWithSource,
    ) => void,
  ) {
    this.subscribeTo(
      this.rootShortcutService.getEvent$(shortcutName),
      (eventInfo) => {
        handleEventCallback(eventInfo.eventTrackingValue);
      },
    );
  }

  private getFusionManager() {
    const fusionOutput = this.taskWorkspaceStore
      .state()
      .project.outputs.find(isSensorFusionOutput);
    assert(fusionOutput, 'Must have fusion output in objects');
    return this.fusionManagerFactory.getManager(fusionOutput.id);
  }

  private getPointCloudSelectedShapes() {
    const pointCloudOutput = this.getFusionManager().getPrimaryMinion().output;

    const selectedShapes = this.selectionService.getNestedSelection();
    const pointCloudInfoState =
      this.taskWorkspaceStore.state().outputAnnotationInfo[
        pointCloudOutput.name
      ];
    assert(pointCloudInfoState, 'output must have annotation info');

    return filterSelectionByOutput(pointCloudInfoState, selectedShapes);
  }
}
